import { useState, useEffect } from "react";
import { View, ScrollView, StyleSheet, TextInput, Platform, ActivityIndicator, Alert } from "react-native";
import { router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { ModernCard } from "@/components/crew";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { DateInput } from "@/components/ui/DateInput";
import { InlineDropdown } from "@/components/ui/InlineDropdown";
import { listService, IncidentType as IncidentTypeList } from "@/services/listService";
import { supabase } from "@/lib/supabase-client";

interface ShiftIncidentScreenProps {
  shiftId: string;
}

export default function ShiftIncidentScreen({ shiftId }: ShiftIncidentScreenProps) {
  const { colors } = useTheme();

  // Incident types loading
  const [incidentTypesLoading, setIncidentTypesLoading] = useState(true);
  const [incidentTypes, setIncidentTypes] = useState<IncidentTypeList[]>([]);

  // Form state
  const [incidentTypeId, setIncidentTypeId] = useState("");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState<Date>(new Date());
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Fetch incident types
  useEffect(() => {
    let alive = true;
    (async () => {
      setIncidentTypesLoading(true);
      try {
        const types = await listService.getIncidentTypes();
        if (alive) setIncidentTypes(types);
      } catch (e) {
        console.error("Failed to load incident types:", e);
      } finally {
        if (alive) setIncidentTypesLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const handleSubmit = async () => {
    if (!incidentTypeId) {
      Alert.alert("Required Field", "Please select an incident type.");
      return;
    }
    if (!title.trim()) {
      Alert.alert("Required Field", "Please enter a title for the incident.");
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("submit-incident", {
        body: {
          shift_id: shiftId,
          incident_type_id: incidentTypeId,
          name: title.trim(),
          description: description.trim() || undefined,
          occurred_date: date.toISOString(),
        },
      });

      if (error) {
        console.error("[INCIDENT] Submit error:", error);
        Alert.alert("Error", error.message || "Failed to submit incident.");
        return;
      }

      if (data?.success) {
        Alert.alert("Incident Reported", `Incident ${data.incident_number} has been created.`, [
          { text: "OK", onPress: () => router.dismiss() },
        ]);
      } else {
        Alert.alert("Error", data?.error || "Unexpected response from server.");
      }
    } catch (e: any) {
      console.error("[INCIDENT] Submit exception:", e);
      Alert.alert("Error", e?.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    router.dismiss();
  };

  if (incidentTypesLoading) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.backgroundSecondary }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  const incidentTypeOptions = incidentTypes.map((type) => ({
    label: type.name,
    value: type.id,
  }));

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}
      contentContainerStyle={styles.content}
      keyboardShouldPersistTaps="handled"
    >
      {/* Section 1: Incident Type */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="alert-circle" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Incident Type
          </ThemedText>
        </View>
        <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
          Select the type of incident being reported.
        </ThemedText>
        <InlineDropdown
          options={incidentTypeOptions}
          value={incidentTypeId}
          onSelect={setIncidentTypeId}
          placeholder="Select incident type..."
        />
      </ModernCard>

      {/* Section 2: Title */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="format-title" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Title
          </ThemedText>
        </View>
        <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
          Provide a brief title for this incident.
        </ThemedText>
        <TextInput
          style={[
            styles.fieldInput,
            {
              color: colors.text,
              backgroundColor: colors.background,
              borderColor: colors.border,
            },
          ]}
          value={title}
          onChangeText={setTitle}
          placeholder="Brief incident title..."
          placeholderTextColor={colors.placeholder}
        />
      </ModernCard>

      {/* Section 3: Date */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="calendar" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Date & Time
          </ThemedText>
        </View>
        <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
          When did this incident occur?
        </ThemedText>
        <DateInput value={date} onChange={setDate} mode="datetime" label="Date & Time" />
      </ModernCard>

      {/* Section 4: Description */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="text-box-outline" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Description
          </ThemedText>
        </View>
        <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
          Provide details about what happened.
        </ThemedText>
        <TextInput
          style={[
            styles.notesInput,
            {
              color: colors.text,
              backgroundColor: colors.background,
              borderColor: colors.border,
            },
          ]}
          value={description}
          onChangeText={setDescription}
          placeholder="Describe the incident in detail..."
          placeholderTextColor={colors.placeholder}
          multiline
          numberOfLines={6}
          textAlignVertical="top"
        />
      </ModernCard>

      {/* Action Buttons */}
      <View style={styles.buttonRow}>
        <Button text="Cancel" icon="close" iconPosition="left" onPress={handleCancel} style={styles.button} />
        <Button
          text={submitting ? "Submitting..." : "Report Incident"}
          icon="check"
          iconPosition="left"
          onPress={handleSubmit}
          disabled={submitting}
          style={[styles.button, { backgroundColor: colors.primary }]}
          textStyle={{ color: "white" }}
        />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  center: {
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    padding: 16,
    paddingBottom: 32,
    gap: 16,
  },
  card: {},
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  fieldInput: {
    height: 44,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
    ...(Platform.OS === "web" ? { outlineStyle: "none" as any } : {}),
  },
  notesInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 120,
    ...(Platform.OS === "web" ? { outlineStyle: "none" as any } : {}),
  },
  buttonRow: {
    flexDirection: "row",
    gap: 12,
  },
  button: {
    flex: 1,
    height: 48,
  },
});
